import * as React from 'react';

import { tokens, type ButtonSizeKey, type ButtonVariantKey } from '../../styles/tokens';

type CommonButtonProps = {
  size: ButtonSizeKey;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
};

type PrimaryButtonProps = CommonButtonProps & {
  variant: 'primary';
  weight?: 'bold' | 'semibold';
};

type SecondaryButtonProps = CommonButtonProps & {
  variant: 'secondary';
  weight?: 'bold' | 'medium';
};

export type ButtonProps = PrimaryButtonProps | SecondaryButtonProps;

const heightClass: Record<ButtonSizeKey, string> = {
  lg: 'h-16',
  md: 'h-12',
  sm: 'h-10',
  xs: 'h-8',
};

const textStyleClass: Record<ButtonSizeKey, string> = {
  lg: 'text-button-large',
  md: 'text-button-mid',
  sm: 'text-button-mid',
  xs: 'text-button-small',
};

const variantClass: Record<ButtonVariantKey, { base: string; hover: string }> = {
  primary: {
    base: 'bg-brand-blue text-neutral-white shadow-button-primary',
    hover: 'hover:bg-brand-deep hover:shadow-button-primary-hover',
  },
  secondary: {
    base: 'bg-neutral-white text-brand-blue border border-brand-blue',
    hover: 'hover:bg-brand-blue hover:text-neutral-white',
  },
};

const weightClass = (weight: 'bold' | 'semibold' | 'medium'): string => {
  switch (weight) {
    case 'bold':
      return 'font-bold';
    case 'semibold':
      return 'font-semibold';
    case 'medium':
      return 'font-medium';
  }
};

const defaultWeight = (variant: ButtonVariantKey) =>
  tokens.button.variants[variant].allowedWeights[0];

export const Button: React.FC<ButtonProps> = (props) => {
  const { variant, size, children, onClick, type = 'button', disabled } = props;
  const weight = props.weight ?? defaultWeight(variant);

  const className = [
    'inline-flex items-center justify-center px-6 rounded-sm transition-colors',
    'font-primary',
    heightClass[size],
    textStyleClass[size],
    weightClass(weight),
    variantClass[variant].base,
    variantClass[variant].hover,
  ].join(' ');

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={className}>
      {children}
    </button>
  );
};
